import { checkDeviceReadiness } from "../utils/checkCompatibility";
import { setupDevices } from "../utils/setupDevice";
import { VideoRecorderHelper } from "./VideoRecorderHelper";

export class VideoRecordingManager {
  constructor() {
    this.compatibility = null;
    this.devices = null;
    this.stream = null;
    this.recorder = null;
    this.isInitialized = false;
  }

  async checkCompatibility() {
    console.trace("Checking compatibility");
    try {
      this.compatibility = await checkDeviceReadiness();
      console.log("Compatibility checked:", this.compatibility);
    } catch (error) {
      console.error("Device not compatible:", error);
      throw new Error(error);
    }
  }

  async askForPermissions() {
    try {
      // Only get new stream if we don't have one
      if (!this.stream) {
        const { devices, stream } = await setupDevices();
        this.devices = devices;
        this.stream = stream;
        console.log("Devices and stream set up:", devices, stream);
      }
      return { devices: this.devices, stream: this.stream };
    } catch (error) {
      console.error("Failed to setup devices:", error);
      throw new Error("Failed to setup devices: " + error.message);
    }
  }

  async initializeRecorderHelper() {
    try {
      if (!this.stream) {
        console.error("No stream available for recorder initialization");
        return false;
      }

      // Only create new recorder if we don't have one
      if (!this.recorder) {
        this.recorder = new VideoRecorderHelper(this.stream);
        console.log("VideoRecorderHelper initialized:", this.recorder);
      }

      return true;
    } catch (error) {
      console.error("Failed to initialize recorder:", error);
      this.recorder = null;
      return false;
    }
  }

  async prepareNewRecording() {
    try {
      // Only get new stream if we don't have one
      if (!this.stream) {
        const { devices, stream } = await setupDevices();
        this.devices = devices;
        this.stream = stream;
      }

      // Only initialize recorder if we don't have one
      if (!this.recorder) {
        const initialized = await this.initializeRecorderHelper();
        if (!initialized) {
          throw new Error("Failed to initialize recorder");
        }
      }

      return { stream: this.stream };
    } catch (error) {
      console.error("Failed to prepare new recording:", error);
      throw error;
    }
  }

  startRecording() {
    if (!this.recorder) {
      console.error("Can't start recording: Recorder not initialized");
      throw new Error("Recorder not initialized");
    }
    if (!this.stream) {
      console.error("No stream available for recording");
      throw new Error("No stream available for recording");
    }
    try {
      this.recorder.startRecording();
    } catch (error) {
      console.error("Error starting recording:", error);
      throw new Error("Failed to start recording: " + error.message);
    }
  }

  async capturePoster(videoRef) {
    if (!videoRef.current) return null;

    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoRef.current, 0, 0);

    return canvas.toDataURL("image/jpeg");
  }

  async stopRecording() {
    if (!this.recorder) {
      console.error("Can't stop recording: Recorder not initialized");
      throw new Error("Recorder not initialized");
    }
    try {
      const videoBlob = await this.recorder.stopRecording();
      console.log("Recording stopped, blob size:", videoBlob.size);
      return videoBlob;
    } catch (error) {
      console.error("Error stopping recording:", error);
      throw new Error("Failed to stop recording: " + error.message);
    }
  }

  async cleanup() {
    // Only cleanup when component unmounts
    if (this.recorder) {
      try {
        console.log("Cleaning up recorder:", this.recorder.recorder?.state);
        if (this.recorder.recorder?.state !== "inactive") {
          await new Promise((resolve, reject) => {
            this.recorder.recorder.onstop = () => {
              console.log("MediaRecorder stopped successfully");
              resolve();
            };
            this.recorder.recorder.onerror = (error) => {
              console.error("MediaRecorder error:", error);
              reject(error);
            };
            this.recorder.recorder.stop();
          });
        }
        this.recorder = null;
      } catch (err) {
        console.error("Error cleaning up recorder:", err);
      }
    }
    if (this.stream) {
      try {
        console.log("Cleaning up stream");
        const tracks = this.stream.getTracks();
        tracks.forEach((track) => {
          console.log(`Stopping track: ${track.kind}`);
          track.stop();
        });
        this.stream = null;
      } catch (err) {
        console.error("Error cleaning up stream:", err);
      }
    }
    console.log("Cleanup completed");
  }
}
